import { resetAnalytics } from "./analytics";
import { clearOutboxes } from "./outbox";
import { endSession } from "./session";

/**
 * The sign-in loop guard. A 401 opens the shared sign-in page once; a second
 * 401 inside the window means sign-in is not fixing it (a misconfigured
 * Gatekeeper, say), so the page stops and offers a button instead of looping.
 */

export const SIGNIN_ATTEMPT_KEY = "carbonos.signin_attempt_at";
/**
 * Training's own marker. The tool list clears the shared one on every 200, so
 * a 401 only training gets would otherwise find it gone and loop.
 */
export const TRAINING_SIGNIN_ATTEMPT_KEY = "carbonos.training_signin_attempt_at";
/** The handbook question's own marker, for the same reason. */
export const HANDBOOK_SIGNIN_ATTEMPT_KEY = "carbonos.handbook_signin_attempt_at";
/**
 * The question in the ask box, kept across the trip to sign-in only: written
 * when Sign in again is pressed, read once and deleted when the page is back.
 */
export const HANDBOOK_QUESTION_KEY = "carbonos.handbook_question";
export const SIGNIN_RETRY_WINDOW_MS = 30_000;
/** When the page last handed somebody Gatekeeper does not know to /auth/refused. */
export const REFUSE_ATTEMPT_KEY = "carbonos.refuse_attempt_at";

type SignInStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;

// Module scope: the page has two data requests, and when both come back 401
// the second must not read the first one's fresh marker as a loop.
let redirecting = false;

/** True once this page has started its trip to sign-in; it is about to be replaced. */
export function isRedirecting(): boolean {
  return redirecting;
}

export function shouldRedirect(now: number, storage: SignInStorage, key = SIGNIN_ATTEMPT_KEY): boolean {
  const last = Number(storage.getItem(key));
  return !last || now - last >= SIGNIN_RETRY_WINDOW_MS;
}

/** Leave the workspace for the shared sign-in page, preserving the destination. */
export function beginSignIn(
  returnTo: string,
  storage: SignInStorage,
  navigate: (url: string) => void,
  now: number = Date.now(),
  key = SIGNIN_ATTEMPT_KEY,
): void {
  storage.setItem(key, String(now));
  redirecting = true;
  navigate(`/sign-in?return=${encodeURIComponent(returnTo)}`);
}

/**
 * Called after a successful load, and before every sign-out, so that signing
 * straight back in after signing out is not mistaken for a loop.
 */
export function clearSignInAttempt(storage: SignInStorage, key = SIGNIN_ATTEMPT_KEY): void {
  storage.removeItem(key);
}

/**
 * A handbook question kept for sign-in comes back only on the Handbook page.
 * Anywhere else (sign-in given up, another page opened) it is dropped on load,
 * so it never outlives the trip.
 */
export function forgetHandbookQuestionAway(pathname: string, storage: () => SignInStorage): void {
  if (pathname === "/handbook" || pathname.startsWith("/handbook/")) return;
  try {
    storage().removeItem(HANDBOOK_QUESTION_KEY);
  } catch {
    // Blocked browser storage kept nothing to forget.
  }
}

/**
 * Everything a sign-out link (and Switch account) does before its navigation:
 * clear the loop guard and any handbook question kept for sign-in, forget the
 * person in analytics, and drop any video progress still waiting to be sent,
 * so the next person on this browser starts clean and is never credited with it.
 */
export function prepareSignOut(storage: SignInStorage): void {
  try {
    clearSignInAttempt(storage);
    clearSignInAttempt(storage, TRAINING_SIGNIN_ATTEMPT_KEY);
    clearSignInAttempt(storage, HANDBOOK_SIGNIN_ATTEMPT_KEY);
    storage.removeItem(HANDBOOK_QUESTION_KEY);
  } catch {
    // Blocked browser storage must not prevent sign-out or the other cleanup.
  }
  resetAnalytics();
  clearOutboxes();
}

/** Called only after confirmation on the shared sign-out page. */
export async function signOut(): Promise<void> {
  try {
    prepareSignOut(window.sessionStorage);
  } catch {
    // Accessing sessionStorage itself can throw in restricted browsers.
    resetAnalytics();
    clearOutboxes();
  }
  // This cookie is HttpOnly, and Clerk's successful browser sign-out bypasses
  // /auth/signout. Clear our selection before either sign-out path can leave.
  const cleared = await fetch("/api/admin/view-as", { method: "DELETE", credentials: "same-origin" });
  if (!cleared.ok) throw new Error("We couldn't sign you out. Please try again.");
  if (await endSession()) return;
  // Do not refresh an expired session while trying to end it.
  const res = await fetch("/auth/signout", { method: "POST", credentials: "same-origin" });
  if (!res.ok) throw new Error("We couldn't sign you out. Please try again.");
  window.location.assign("/signed-out");
}

/**
 * Hands somebody Gatekeeper does not know to the server, which ends their
 * session and sends them to the sign-in page saying why. A document
 * navigation: revoking needs the server, and the page must not stay.
 */
export function refuseUnknown(
  navigate: (url: string) => void = (url) => window.location.assign(url),
  storage: SignInStorage = window.sessionStorage,
  now: number = Date.now(),
): void {
  // Once per window: if the server could not confirm it (Gatekeeper slow) it
  // sends them back, and the page shows its refusal instead of looping.
  if (!shouldRedirect(now, storage, REFUSE_ATTEMPT_KEY)) return;
  storage.setItem(REFUSE_ATTEMPT_KEY, String(now));
  navigate("/auth/refused");
}
